import axios from 'axios';
import { node } from 'prop-types';
import React, { useContext, createContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { makeSelectUserProfile } from 'containers/App/selectors';
import { LoadingIcon } from 'components/Common';
import { formatToggleValToBoolean } from 'utils';
import { FEATURE_TOGGLE, VIEW_LINKED_ORG } from 'consts';

const Context = createContext({ features: {} });

export const useFeatureToggleContext = () => useContext(Context);

export const useFeatures = (featureIds) => {
  const { features } = useFeatureToggleContext();
  return featureIds.map((id) => features[id]);
};

export const useBooleanFeatures = (featureIds) => {
  const { features } = useFeatureToggleContext();
  return featureIds.map((id) => {
    if (features[id]) {
      return formatToggleValToBoolean(features[id]);
    } else {
      return undefined;
    }
  });
};

export const useGovernanceToggle = () => {
  const [isGovernanceToggleOpen] = useBooleanFeatures([
    FEATURE_TOGGLE.CAN_SEE_GOVERNANCE,
  ]);
  return isGovernanceToggleOpen;
};

export const useCustomFieldToggle = () => {
  const [canSeeCustomField] = useBooleanFeatures([
    FEATURE_TOGGLE.CAN_SEE_CUSTOM_FIELD,
  ]);
  return canSeeCustomField;
};

export const useAvatarBadgeToggle = () => {
  const [canSeeAvatarBadge] = useBooleanFeatures([
    FEATURE_TOGGLE.CAN_SEE_AVATAR_BADGE,
  ]);
  return canSeeAvatarBadge;
};

export const useChatAuditToggle = () => {
  const [canSeeChatAudit] = useBooleanFeatures([
    FEATURE_TOGGLE.CAN_SEE_CHAT_AUDIT,
  ]);
  return canSeeChatAudit;
};

type LinkedOrgToggleReturn = [canSeeLinkedOrg: boolean];
export const useLinkedOrgToggle = (): LinkedOrgToggleReturn => {
  const [canSeeLinkedOrg] = useFeatures([FEATURE_TOGGLE.CAN_SEE_LINKED_ORG]);
  const linkedOrgEnabled = Number(canSeeLinkedOrg) === VIEW_LINKED_ORG.CAN_SEE;
  return [linkedOrgEnabled];
};

const selectUserProfile = makeSelectUserProfile();

interface FeatureToggleProviderProps {
  children: React.ReactNode;
}
export const FeatureToggleProvider: React.FC<FeatureToggleProviderProps> = ({
  children,
}) => {
  const [features, setFeatures] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { organizationCode } = useParams();
  const { employee_id: employeeId } = useSelector(selectUserProfile);

  useEffect(() => {
    if (!employeeId) return;
    setIsLoading(true);
    axios
      // feature toggle
      .post('/api/c3dpdGNocHJv', {
        employee_code: employeeId.toString(),
        organization_code: organizationCode,
      })
      .then((res) => res.data)
      .then(({ tegglos }) => {
        if (!Array.isArray(tegglos)) throw new Error();
        const featureMap = {};
        for (const { tegglo_id, tegglo_val } of tegglos) {
          // eslint-disable-next-line camelcase
          featureMap[tegglo_id] = tegglo_val;
        }
        setFeatures(featureMap);
      })
      .catch(() => {
        /* do nothing */
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [employeeId, organizationCode]);

  let childrenWithProps = children;
  if (isLoading) {
    childrenWithProps = <LoadingIcon />;
  }

  return (
    <Context.Provider value={{ features }}>
      {childrenWithProps}
    </Context.Provider>
  );
};
